<template>
  <el-dialog title="飞书预警策略" :visible.sync="dialogVisible" width="1180px" :close-on-click-modal="false">
    <div v-loading="loading">
      <div class="policy-toolbar">
        <div class="policy-summary">按标签、单双号分别配置预警策略；未配置的类型不预警。</div>
        <el-button type="primary" size="small" :disabled="policies.length >= 10" @click="addPolicy">新增策略</el-button>
      </div>

      <el-table :data="policies" border size="small" class="policy-table">
        <el-table-column label="单双号" width="105">
          <template slot-scope="scope">
            <el-select v-model="scope.row.account_type" :disabled="!!scope.row.id" style="width: 100%">
              <el-option label="单号" value="single"></el-option>
              <el-option label="双号" value="double"></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="标签" min-width="170">
          <template slot-scope="scope">
            <el-select v-model="scope.row.label_name" filterable placeholder="请选择标签" style="width: 100%">
              <el-option
                v-for="item in labelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
                :disabled="isLabelDisabled(item.value, scope.$index)"
              ></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="预警频率" width="150">
          <template slot-scope="scope">
            <el-select v-model="scope.row.frequency" placeholder="请选择" style="width: 100%" @change="handleFrequencyChange(scope.row)">
              <el-option v-for="item in frequencyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="预警阈值" width="155">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.alert_threshold"
              :min="1"
              :max="999999"
              :step="1"
              :precision="0"
              controls-position="right"
              style="width: 115px"
            ></el-input-number>
            <span class="threshold-unit">人</span>
          </template>
        </el-table-column>
        <el-table-column label="每周发送星期" width="145">
          <template slot-scope="scope">
            <el-select v-if="scope.row.frequency === 'weekly'" v-model="scope.row.weekday" placeholder="请选择" style="width: 100%">
              <el-option v-for="item in weekdayOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <span v-else class="not-applicable">—</span>
          </template>
        </el-table-column>
        <el-table-column label="发送时间" width="150">
          <template slot-scope="scope">
            <el-time-picker
              v-if="scope.row.frequency === 'daily' || scope.row.frequency === 'weekly'"
              v-model="scope.row.send_time"
              format="HH:mm"
              value-format="HH:mm:ss"
              placeholder="请选择"
              style="width: 120px"
            ></el-time-picker>
            <span v-else-if="scope.row.frequency === 'threshold'">即时</span>
            <span v-else class="not-applicable">—</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-switch v-model="scope.row.status" :active-value="1" :inactive-value="0" active-text="" inactive-text=""></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" :disabled="policies.length === 1" @click="removePolicy(scope.$index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="policy-hint">
        <div class="policy-hint-title">统计与触发口径</div>
        <div>按负责客服 + 记录保存的单双号 + 标签 + 对应周期聚合，玩家账号去重。</div>
        <div>达到阈值：每达到一个阈值倍数即时发送，例如 5、10、15 人。</div>
        <div>每日、每周达到阈值后，按配置的发送时间汇总发送。</div>
        <div>同一标签可分别配置单号、双号策略，人数分别累计、分别提醒。</div>
        <div>双号仅统计首次启用后新增的记录；修改阈值保留累计人数，历史记录不重新识别单双号。</div>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getQwAiQualityFeishuAlertPolicyApi, saveQwAiQualityFeishuAlertPolicyApi } from '@/api/qwAiQuality';

export default {
  props: {
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      saving: false,
      policies: [],
      localKey: 1,
      frequencyOptions: [
        { value: 'threshold', label: '达到预警阈值' },
        { value: 'daily', label: '每日' },
        { value: 'weekly', label: '每周' },
      ],
      weekdayOptions: [
        { value: 1, label: '周一' },
        { value: 2, label: '周二' },
        { value: 3, label: '周三' },
        { value: 4, label: '周四' },
        { value: 5, label: '周五' },
        { value: 6, label: '周六' },
        { value: 7, label: '周日' },
      ],
    };
  },
  computed: {
    labelOptions() {
      return this.options.labels || [];
    },
  },
  methods: {
    async open() {
      this.dialogVisible = true;
      this.loading = true;
      try {
        const data = await getQwAiQualityFeishuAlertPolicyApi();
        if (data.code !== 200) {
          this.$message.error(data.message);
          return;
        }
        const policies = (data.data && data.data.policies) || [];
        this.policies = policies.length ? policies.map(item => this.normalizePolicy(item)) : [this.createEmptyPolicy()];
      } catch (error) {
        this.$message.error('获取飞书预警策略失败');
      } finally {
        this.loading = false;
      }
    },
    createEmptyPolicy() {
      return {
        _key: this.localKey++,
        id: null,
        label_name: '',
        account_type: 'single',
        frequency: '',
        alert_threshold: null,
        weekday: null,
        send_time: null,
        status: 0,
      };
    },
    normalizePolicy(policy) {
      return {
        _key: this.localKey++,
        id: policy.id,
        label_name: policy.label_name || '',
        account_type: policy.account_type || 'single',
        frequency: policy.frequency || '',
        alert_threshold: Number(policy.alert_threshold) || 1,
        weekday: policy.weekday === null ? null : Number(policy.weekday),
        send_time: policy.send_time || null,
        status: Number(policy.status) === 1 ? 1 : 0,
      };
    },
    addPolicy() {
      if (this.policies.length >= 10) {
        this.$message.warning('飞书预警策略最多配置10条');
        return;
      }
      this.policies.push(this.createEmptyPolicy());
    },
    removePolicy(index) {
      if (this.policies.length === 1) {
        this.$message.warning('至少保留一条飞书预警策略');
        return;
      }
      this.policies.splice(index, 1);
    },
    handleFrequencyChange(policy) {
      if (policy.frequency === 'threshold') {
        policy.weekday = null;
        policy.send_time = null;
      } else if (policy.frequency === 'daily') {
        policy.weekday = null;
      }
    },
    isLabelDisabled(labelName, currentIndex) {
      const accountType = this.policies[currentIndex].account_type;
      return this.policies.some((policy, index) => index !== currentIndex && policy.label_name === labelName
        && policy.account_type === accountType);
    },
    validatePolicies() {
      const labels = [];
      for (let index = 0; index < this.policies.length; index += 1) {
        const policy = this.policies[index];
        if (!policy.label_name) return '请选择第' + (index + 1) + '条策略的标签';
        if (['single', 'double'].indexOf(policy.account_type) === -1) return '请选择单双号';
        const key = policy.label_name + ':' + policy.account_type;
        if (labels.indexOf(key) !== -1) return '同一标签、同一单双号仅允许配置一条预警策略';
        labels.push(key);
        if (!policy.frequency) return '请选择第' + (index + 1) + '条策略的预警频率';
        const threshold = Number(policy.alert_threshold);
        if (!Number.isInteger(threshold) || threshold < 1 || threshold > 999999) return '预警阈值必须是1至999999的整数';
        if ((policy.frequency === 'daily' || policy.frequency === 'weekly') && !policy.send_time) return '每日、每周策略必须配置发送时间';
        if (policy.frequency === 'weekly' && !policy.weekday) return '每周策略必须选择发送星期';
      }
      return '';
    },
    async save() {
      const errorMessage = this.validatePolicies();
      if (errorMessage) {
        this.$message.warning(errorMessage);
        return;
      }
      this.saving = true;
      try {
        const policies = this.policies.map(policy => ({
          id: policy.id,
          label_name: policy.label_name,
          account_type: policy.account_type,
          frequency: policy.frequency,
          alert_threshold: Number(policy.alert_threshold),
          weekday: policy.frequency === 'weekly' ? policy.weekday : null,
          send_time: policy.frequency === 'daily' || policy.frequency === 'weekly' ? policy.send_time : null,
          status: policy.status,
        }));
        const data = await saveQwAiQualityFeishuAlertPolicyApi({ policies });
        if (data.code !== 200) {
          this.$message.error(data.message);
          return;
        }
        this.$message.success('飞书预警策略已保存');
        this.dialogVisible = false;
      } catch (error) {
        this.$message.error('保存飞书预警策略失败');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.policy-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.policy-summary,
.not-applicable {
  color: #8792a2;
}
.policy-table {
  width: 100%;
}
.threshold-unit {
  margin-left: 4px;
}
.policy-hint {
  margin-top: 16px;
  padding: 14px 18px;
  color: #606a78;
  line-height: 1.8;
  background: #f7f9fc;
  border: 1px solid #e6ebf2;
  border-radius: 4px;
}
.policy-hint-title {
  margin-bottom: 4px;
  color: #263447;
  font-weight: 700;
}
</style>
